import { transformEnumToDisplay } from '@fruitful/member-profile';
import { useAtomValue } from 'jotai';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import { useState } from 'react';
import { H6Emphasized, Spinner, useToastController, YStack } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import { type MemberDetails } from '../../entities/entityTypes';
import { InvestmentsEditor } from '../../member/components/editors/InvestmentsEditor';
import { useUpdateMemberAndRefetch } from '../../member/hooks/useUpdateMember';

interface MembershipInvestmentsTableProps {
    members: MemberDetails[];
}

export const MembershipInvestmentsTable = ({
    members,
}: MembershipInvestmentsTableProps) => {
    const toast = useToastController();

    const adminUser = useAtomValue(adminUserAtom);
    const canEditMember =
        adminUser.authorization.selectedMembership.general.canEdit;

    const membership = useAtomValue(selectedMembershipAtom);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const { updateMemberAsync } = useUpdateMemberAndRefetch(membership);

    const onRowEditComplete = async ({
        newData,
    }: DataTableRowEditCompleteEvent) => {
        setIsSubmitting(true);
        const member: MemberDetails = newData as MemberDetails;

        try {
            const upsertMemberBody = {
                investments: member.investments,
            };
            await updateMemberAsync(member.id, upsertMemberBody);

            toast.show('Investments updated!', MutationSuccessToastOptions);
        } catch (error) {
            toast.show(
                'Error updating investments',
                MutationErrorToastOptions(),
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <YStack gap={12}>
            <H6Emphasized>Investments details</H6Emphasized>
            {isSubmitting ? (
                <Spinner />
            ) : (
                <DataTable
                    value={members}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: 13 }}
                    editMode="row"
                    onRowEditComplete={onRowEditComplete}
                    scrollable
                >
                    <Column field="id" header="id" hidden={true} />
                    <Column field="firstName" header="First Name" />
                    <Column field="lastName" header="Last Name" />
                    <Column
                        field="investments"
                        header="Investments"
                        body={({ investments }: MemberDetails) => {
                            return investments != null
                                ? investments
                                      .map(transformEnumToDisplay)
                                      .join(', ')
                                : '';
                        }}
                        editor={(options) => InvestmentsEditor(options)}
                        style={{
                            minWidth: 250,
                        }}
                    />
                    <Column rowEditor hidden={!canEditMember} />
                </DataTable>
            )}
        </YStack>
    );
};
